import { Button, Input, Modal, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { type CSSProperties, useMemo, useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../../../client/gql";

// Colocated fragment: the picker only needs the org's id and name.
const OrgPickerModal_Organization = graphql(`
  fragment OrgPickerModal_Organization on Organization {
    uuid
    name
  }
`);

const OrgPickerModalQuery = graphql(`
  query OrgPickerModal_Query {
    organizations {
      ...OrgPickerModal_Organization
    }
  }
`);

type Props = {
  title: string;
  /** uuids already linked — excluded from the pick list. */
  excludeUuids: string[];
  /** show a "licensed modules" input and pass them back. */
  withModules?: boolean;
  onClose: () => void;
  onPick: (uuid: string, modules?: string[]) => void;
};

const field: CSSProperties = { marginBottom: 14 };

export function OrgPickerModal({ title, excludeUuids, withModules, onClose, onPick }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [modules, setModules] = useState("");
  const [{ data }] = useQuery({ query: OrgPickerModalQuery });
  const orgs = getFragmentData(OrgPickerModal_Organization, data?.organizations ?? []);

  const exclude = useMemo(() => new Set(excludeUuids), [excludeUuids]);
  const options: SelectOptionType[] = useMemo(
    () => orgs.filter((o) => !exclude.has(o.uuid)).map((o) => ({ value: o.uuid, label: o.name })),
    [orgs, exclude],
  );

  const confirm = () => {
    if (!selected) {
      return;
    }
    const mods = modules
      .split(/[\s,]+/)
      .map((m) => m.trim())
      .filter(Boolean);
    onPick(selected, withModules ? mods : undefined);
  };

  return (
    <Modal isOpen ariaHideApp={false} title={title} onRequestClose={onClose}>
      <div style={field}>
        <Select
          name="org-pick"
          label="Organisation"
          options={options}
          isSearchable
          placeholder="Organisation suchen …"
          onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) =>
            setSelected(opt ? String((opt as SelectOptionType).value) : null)
          }
        />
      </div>
      {withModules && (
        <div style={field}>
          <Input
            name="org-pick-modules"
            label="Lizenzierte Module (optional, kommagetrennt)"
            value={modules}
            onChange={(e) => setModules(e.target.value)}
          />
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "var(--spacing-2)",
          marginTop: "var(--spacing-4)",
        }}
      >
        <Button hierarchy="tertiary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button hierarchy="primary" disabled={!selected} onClick={confirm}>
          Hinzufügen
        </Button>
      </div>
    </Modal>
  );
}
